import { useState } from 'react';
import { Library, Waves, X } from 'lucide-react';
import { Input } from '../ui/input';
import { CineCombo } from './CineCombo';
import { AssetPicker } from './assets/AssetPicker';
import { absUrl } from '../../lib/aiStudioApi';

const Label = ({ children }) => <div className="mb-1 text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-400">{children}</div>;
const iconBtn = 'flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-neutral-700 text-neutral-300 transition-colors duration-200 hover:border-amber-400 hover:text-amber-200';

/** Free text with an Asset Library shortcut; on shots the picked image also becomes a reference for the frame. */
function LibraryText({ label, field, folder, value, url, onChange, withFile, placeholder, testId }) {
  const [open, setOpen] = useState(false);
  const urlField = `${field}_url`;
  return (
    <div>
      <Label>{label}</Label>
      <div className="flex gap-1.5">
        {url && <img src={absUrl(url)} alt="" className="h-9 w-9 shrink-0 rounded border border-neutral-700 object-cover" data-testid={`${testId}-thumb`} />}
        <Input value={value || ''} onChange={(e) => onChange({ [field]: e.target.value })} placeholder={placeholder}
          className="h-9 border-neutral-700 bg-neutral-950 text-xs text-neutral-100 placeholder:text-neutral-500" data-testid={testId} />
        <button type="button" onClick={() => setOpen(true)} title="Pick from your Asset Library" className={iconBtn} data-testid={`${testId}-library`}><Library size={14} /></button>
        {url && <button type="button" onClick={() => onChange({ [urlField]: '' })} title="Stop using this image" className="px-1 text-neutral-500 hover:text-red-300" data-testid={`${testId}-clear-file`}><X size={13} /></button>}
      </div>
      <AssetPicker open={open} onOpenChange={setOpen} folders={[folder]} title={`Pick ${label.toLowerCase()} from your Asset Library`}
        onPick={(a) => onChange({ [field]: [a.name, a.notes].filter(Boolean).join(' — '), ...(withFile ? { [urlField]: a.url } : {}) })} />
    </div>
  );
}

function SoundFile({ url, name, onChange, testId }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="flex items-center gap-1.5" data-testid={testId}>
      <button type="button" onClick={() => setOpen(true)} className="flex items-center gap-1 text-[11px] text-neutral-300 transition-colors hover:text-amber-200" data-testid={`${testId}-pick`}>
        <Waves size={12} /> {url ? 'Change sound file' : 'Use my own sound file'}
      </button>
      {url && <audio src={absUrl(url)} controls preload="none" className="h-7 min-w-0 flex-1" data-testid={`${testId}-audio`} />}
      {url && <button type="button" onClick={() => onChange({ sfx_url: '' })} className="text-neutral-500 hover:text-red-300" data-testid={`${testId}-clear`}><X size={12} /></button>}
      <AssetPicker open={open} onOpenChange={setOpen} folders={['sfx']} title="Pick a sound effect from your Asset Library"
        onPick={(a) => onChange({ sfx_url: a.url, sfx: name || a.name })} />
    </div>
  );
}

/** Professional scene settings: scene type (400+ world-cinema scenes), time, weather, mood, emotion, blocking, wardrobe, props, sound.
 *  Every field accepts the director's own words. `withFiles` (shots) also links Asset Library images / sound files. */
export function SceneSetupFields({ catalog, value, onChange, testPrefix, withFiles = false }) {
  const sc = catalog.scene || {};
  const combo = (key, label, options, extra = {}) => (
    <CineCombo label={label} value={value[key] || ''} options={options} onChange={(v) => onChange({ [key]: v })} allowCustom
      placeholder="Auto — the AI decides" clearLabel="Clear" testId={`${testPrefix}-${key.replace(/_/g, '-')}`} {...extra} />
  );
  return (
    <div className="space-y-3" data-testid={`${testPrefix}-scene-setup`}>
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {combo('scene_type', `Scene type (${sc.scene_type_count || 400}+)`, undefined, { groups: sc.scene_types || [] })}
        {combo('time_of_day', 'Time of day', sc.times_of_day || [])}
        {combo('weather', 'Weather', sc.weather || [])}
        {combo('mood', 'Mood', sc.moods || [])}
        {combo('emotion', 'Character emotion', sc.emotions || [])}
        {combo('blocking', 'Blocking (who stands / moves where)', sc.blocking || [])}
      </div>
      <div className="grid gap-3 md:grid-cols-3">
        <LibraryText label="Wardrobe" field="wardrobe" folder="wardrobe" value={value.wardrobe} url={withFiles ? value.wardrobe_url : ''} onChange={onChange}
          withFile={withFiles} placeholder="e.g. faded barong, rain-soaked denim jacket" testId={`${testPrefix}-wardrobe`} />
        <LibraryText label="Props & accessories" field="props" folder="props" value={value.props} url={withFiles ? value.props_url : ''} onChange={onChange}
          withFile={withFiles} placeholder="e.g. grandmother's recipe book, pearl earrings" testId={`${testPrefix}-props`} />
        <div className="space-y-1.5">
          {combo('sfx', 'Sound effects', sc.sfx || [])}
          {withFiles && <SoundFile url={value.sfx_url} name={value.sfx} onChange={onChange} testId={`${testPrefix}-sfx-file`} />}
        </div>
      </div>
    </div>
  );
}

export const SCENE_KEYS = ['scene_type', 'time_of_day', 'weather', 'mood', 'emotion', 'blocking', 'wardrobe', 'props', 'sfx'];
export const SHOT_FILE_KEYS = ['wardrobe_url', 'props_url', 'sfx_url'];
